import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Chip,
  Grid,
  TableSortLabel,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { showsAPI, horsesAPI, referenceAPI, trainersAPI } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import AddHorseModal from '../components/AddHorseModal';
import AddTrainerModal from '../components/AddTrainerModal';
import AddReferenceModal from '../components/AddReferenceModal';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { cardSx } from '../theme';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows, useSkeletonAnimation } from '../components/LoadingSkeletons';

// Placeholder for one stat figure, centred like the number it stands in for.
const StatBone = () => <Skeleton animation={useSkeletonAnimation()} width="4ch" sx={{ mx: 'auto' }} />;

const ShowsPage = () => {
  const notify = useNotify();
  const [showRecords, setShowRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Filter states
  const [filterHorse, setFilterHorse] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  
  // Sort states
  const [sortBy, setSortBy] = useState('show_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Reference data
  const [horses, setHorses] = useState([]);
  const [showEvents, setShowEvents] = useState([]);
  const [showNames, setShowNames] = useState([]);
  const [showClasses, setShowClasses] = useState([]);
  const [trainers, setTrainers] = useState([]);

  // Modal states
  const [openHorseModal, setOpenHorseModal] = useState(false);
  const [openTrainerModal, setOpenTrainerModal] = useState(false);
  const [openShowEventModal, setOpenShowEventModal] = useState(false);
  const [openShowNameModal, setOpenShowNameModal] = useState(false);
  const [openShowClassModal, setOpenShowClassModal] = useState(false);

  const [formData, setFormData] = useState({
    horse_id: '',
    event_id: '',
    show_name_id: '',
    show_date: '',
    class_id: '',
    score: '',
    placing: '',
    earnings: '',
    rider_id: '',
    notes: '',
  });

  // Load reference data
  useEffect(() => {
    loadReferenceData();
    loadStats();
  }, []);

  const loadReferenceData = async () => {
    try {
      const [horsesRes, showEventsRes, showNamesRes, showClassesRes, trainersRes] = await Promise.all([
        horsesAPI.getAll({ owner_type: 'owned' }),
        referenceAPI.getShowEvents(),
        referenceAPI.getShowNames(),
        referenceAPI.getShowClasses(),
        trainersAPI.getAll({ active: 'true' }),
      ]);
      setHorses(toArray(horsesRes.data, 'horses'));
      setShowEvents(toArray(showEventsRes.data, 'show events'));
      setShowNames(toArray(showNamesRes.data, 'show names'));
      setShowClasses(toArray(showClassesRes.data, 'show classes'));
      setTrainers(toArray(trainersRes.data, 'trainers'));
    } catch (error) {
      console.error('Error loading reference data:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await showsAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadShowRecords = useCallback(async () => {
    setLoadError('');
    try {
      setLoading(true);
      const params = {};
      if (filterHorse) params.horse_id = filterHorse;
      if (filterClass) params.class_id = filterClass;
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;
      params.sort_by = sortBy;
      params.sort_order = sortOrder;
      
      const response = await showsAPI.getAll(params);
      setShowRecords(toArray(response.data, 'show records'));
    } catch (error) {
      console.error('Error loading show records:', error);
      // The previous list is left in place on purpose: a failed refresh should say so
      // rather than blank the page out into something indistinguishable from "no records".
      setLoadError(error?.response?.data?.error || 'Unable to load show records for the selected filters. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filterHorse, filterClass, filterStartDate, filterEndDate, sortBy, sortOrder]);

  useEffect(() => {
    loadShowRecords();
  }, [loadShowRecords]);

  useRefreshOnFocus(() => {
    loadShowRecords();
    loadStats();
  });

  const handleOpenDialog = async (record = null) => {
    // Reload reference data to ensure we have the latest shows
    await loadReferenceData();
    
    if (record) {
      setEditingRecord(record);
      setFormData({
        horse_id: record.horse_id,
        event_id: record.event_id || '',
        show_name_id: record.show_name_id || '',
        show_date: record.show_date || '',
        class_id: record.class_id || '',
        score: record.score || '',
        placing: record.placing || '',
        earnings: record.earnings || '',
        rider_id: record.rider_id || '',
        notes: record.notes || '',
      });
    } else {
      setEditingRecord(null);
      setFormData({
        horse_id: '',
        event_id: '',
        show_name_id: '',
        show_date: '',
        class_id: '',
        score: '',
        placing: '',
        earnings: '',
        rider_id: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRecord(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      // Clear show_name_id when event changes since shows are filtered by event
      if (name === 'event_id') {
        updated.show_name_id = '';
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    try {
      // Convert empty strings to null for optional fields
      const dataToSubmit = { ...formData };
      ['event_id', 'class_id', 'score', 'placing', 'earnings', 'rider_id', 'notes'].forEach(field => {
        if (dataToSubmit[field] === '') {
          dataToSubmit[field] = null;
        }
      });
      // Convert score and earnings to numbers if provided
      if (dataToSubmit.score !== null && dataToSubmit.score !== '') {
        dataToSubmit.score = parseFloat(dataToSubmit.score);
      }
      if (dataToSubmit.earnings !== null && dataToSubmit.earnings !== '') {
        dataToSubmit.earnings = parseFloat(dataToSubmit.earnings);
      }

      if (editingRecord) {
        await showsAPI.update(editingRecord.id, dataToSubmit);
      } else {
        await showsAPI.create(dataToSubmit);
      }
      handleCloseDialog();
      loadShowRecords();
      loadStats();
      loadReferenceData(); // Reload to get new horses/references if added
    } catch (error) {
      console.error('Error saving show record:', error);
      const message = error.response?.data?.error || error.message || 'Error saving show record';
      notify.error(message);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (id) => setDeleteTarget(id);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await showsAPI.delete(deleteTarget);
      setDeleteTarget(null);
      loadShowRecords();
      loadStats();
    } catch (error) {
      console.error('Error deleting show record:', error);
      notify.error(errorMessage(error, 'Error deleting show record'));
    }
  };

  const handleAddHorse = async (newHorse) => {
    await loadReferenceData();
    setFormData((prev) => ({ ...prev, horse_id: newHorse.id }));
    setOpenHorseModal(false);
  };

  const handleAddTrainer = async (newTrainer) => {
    await loadReferenceData();
    setFormData((prev) => ({ ...prev, rider_id: newTrainer.id }));
    setOpenTrainerModal(false);
  };

  const handleAddShowEvent = async (newEvent) => {
    await loadReferenceData();
    setFormData((prev) => ({ ...prev, event_id: newEvent.id }));
    setOpenShowEventModal(false);
  };

  const handleAddShowName = async (newShowName) => {
    await loadReferenceData();
    setFormData((prev) => ({ ...prev, show_name_id: newShowName.id }));
    setOpenShowNameModal(false);
  };

  const handleCreateShowName = async (data) => {
    // Include event_id from form if available
    if (formData.event_id) {
      data.event_id = formData.event_id;
    }
    const response = await referenceAPI.createShowName(data);
    return response;
  };

  const handleAddShowClass = async (newClass) => {
    await loadReferenceData();
    setFormData((prev) => ({ ...prev, class_id: newClass.id }));
    setOpenShowClassModal(false);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getPlacingColor = (placing) => {
    if (!placing) return 'default';
    const placingLower = placing.toLowerCase();
    if (placingLower.includes('1st') || placingLower.includes('champion')) {
      return 'success';
    }
    if (placingLower.includes('2nd') || placingLower.includes('reserve')) {
      return 'info';
    }
    if (placingLower.includes('3rd')) {
      return 'warning';
    }
    return 'default';
  };

  // Calculate total earnings from filtered/displayed records
  const calculateTotalEarnings = () => {
    return showRecords.reduce((sum, record) => {
      const earnings = parseFloat(record.earnings) || 0;
      return sum + earnings;
    }, 0);
  };

  const filteredTotalEarnings = calculateTotalEarnings();

  const getShowHorseLabel = (horse) => horse.name || horse.registered_name || `Horse ID: ${horse.id}`;

  const filterHorseOptions = useMemo(() => [
    { id: '', label: 'All Horses' },
    ...horses.map((horse) => ({ id: horse.id, label: getShowHorseLabel(horse) })),
  ], [horses]);

  const filterClassOptions = useMemo(() => [
    { id: '', label: 'All Classes' },
    ...showClasses.map((showClass) => ({ id: showClass.id, label: showClass.name })),
  ], [showClasses]);

  const filteredShowNames = useMemo(() => showNames.filter((show) => {
    // If no event is selected, show all shows
    if (!formData.event_id || formData.event_id === '') return true;

    // Convert both to numbers for comparison (handle string/number mismatches)
    const selectedEventId = Number(formData.event_id);
    const showEventId = show.event_id != null ? Number(show.event_id) : null;

    // Show if event_id matches
    const matches = showEventId === selectedEventId;

    // Debug logging in development
    if (process.env.NODE_ENV === 'development' && formData.event_id) {
      console.log(`Show "${show.name}": event_id=${show.event_id} (${typeof show.event_id}), selected=${selectedEventId}, matches=${matches}`);
    }

    return matches;
  }), [showNames, formData.event_id]);

  const riderOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...trainers.map((trainer) => {
      let label = '';
      if (trainer.trainer_name && trainer.business_name) {
        label = `${trainer.trainer_name} — ${trainer.business_name}`;
      } else if (trainer.trainer_name) {
        label = trainer.trainer_name;
      } else if (trainer.business_name) {
        label = trainer.business_name;
      } else {
        label = `Trainer #${trainer.id}`;
      }
      return { id: trainer.id, label };
    }),
  ], [trainers]);

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Show Records</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Show Record
        </Button>
      </Box>

      {/* Statistics — the four cards hold their place while the figures load. */}
      {(stats || loading) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ ...cardSx, p: 2, textAlign: 'center' }}>
              <Typography variant="h6">{stats ? stats.total_shows : <StatBone />}</Typography>
              <Typography variant="body2" color="textSecondary">Total Shows</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ ...cardSx, p: 2, textAlign: 'center' }}>
              <Typography variant="h6">{stats ? stats.wins : <StatBone />}</Typography>
              <Typography variant="body2" color="textSecondary">Wins</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ ...cardSx, p: 2, textAlign: 'center' }}>
              <Typography variant="h6">{stats ? (stats.total_points?.toFixed(1) || 0) : <StatBone />}</Typography>
              <Typography variant="body2" color="textSecondary">Total Points</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ ...cardSx, p: 2, textAlign: 'center' }}>
              <Typography variant="h6">
                {loading && showRecords.length === 0 ? <StatBone /> : `$${filteredTotalEarnings.toFixed(2)}`}
              </Typography>
              <Typography variant="body2" color="textSecondary">Total Earnings</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ ...cardSx, p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Autocomplete
              fullWidth
              size="small"
              disableClearable
              options={filterHorseOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              filterOptions={createPrefixFilter((o) => o.label)}
              value={filterHorseOptions.find((o) => o.id === filterHorse) ?? filterHorseOptions[0]}
              onChange={(e, newValue) => setFilterHorse(newValue?.id ?? '')}
              renderInput={(params) => <TextField {...params} label="Horse" />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              fullWidth
              size="small"
              disableClearable
              options={filterClassOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.id === v?.id}
              filterOptions={createPrefixFilter((o) => o.label)}
              value={filterClassOptions.find((o) => o.id === filterClass) ?? filterClassOptions[0]}
              onChange={(e, newValue) => setFilterClass(newValue?.id ?? '')}
              renderInput={(params) => <TextField {...params} label="Class" />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              size="small"
              label="Start Date"
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              size="small"
              label="End Date"
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>
      {loadError ? (
        <Alert severity="error" sx={{ mb: 0.5 }}>
          {loadError}
        </Alert>
      ) : null}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'horse_name'}
                  direction={sortBy === 'horse_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('horse_name')}
                >
                  Horse
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'event_name'}
                  direction={sortBy === 'event_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('event_name')}
                >
                  Event
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'show_name'}
                  direction={sortBy === 'show_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('show_name')}
                >
                  Show
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'show_date'}
                  direction={sortBy === 'show_date' ? sortOrder : 'asc'}
                  onClick={() => handleSort('show_date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'class_name'}
                  direction={sortBy === 'class_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('class_name')}
                >
                  Class
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'score'}
                  direction={sortBy === 'score' ? sortOrder : 'asc'}
                  onClick={() => handleSort('score')}
                >
                  Score
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'placing'}
                  direction={sortBy === 'placing' ? sortOrder : 'asc'}
                  onClick={() => handleSort('placing')}
                >
                  Placing
                </TableSortLabel>
              </TableCell>
              <TableCell>Earnings</TableCell>
              <TableCell>Rider</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && showRecords.length === 0 && <TableSkeletonRows columns={10} />}
            {!loading && showRecords.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="textSecondary">No show records found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              showRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell align="center" sx={actionsKabobCellSx}>
                    <RowActionsMenu
                      actions={[
                        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(record) },
                        { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(record.id), color: 'error' },
                      ]}
                    />
                  </TableCell>
                  <TableCell>
                    {record.horse_name || record.horse_registered_name || `Horse ID: ${record.horse_id}`}
                  </TableCell>
                  <TableCell>{record.event_name || '-'}</TableCell>
                  <TableCell>{record.show_name || '-'}</TableCell>
                  <TableCell>
                    {formatDate(record.show_date) ?? '-'}
                  </TableCell>
                  <TableCell>{record.class_name || '-'}</TableCell>
                  <TableCell>{record.score !== null && record.score !== undefined ? record.score : '-'}</TableCell>
                  <TableCell>
                    {record.placing ? (
                      <Chip
                        label={record.placing}
                        size="small"
                        color={getPlacingColor(record.placing)}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {record.earnings ? `$${parseFloat(record.earnings).toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell>{record.rider_name || '-'}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      {/* Add/Edit Show Record Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRecord ? 'Edit Show Record' : 'Add Show Record'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Grid container spacing={2}>
              {/* Horse Selection */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={horses}
                    getOptionLabel={getShowHorseLabel}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter(getShowHorseLabel)}
                    value={horses.find((horse) => horse.id === formData.horse_id) ?? null}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, horse_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Horse" required />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenHorseModal(true)}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Event Selection */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={showEvents}
                    getOptionLabel={(event) => event.name}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((event) => event.name)}
                    value={showEvents.find((event) => event.id === formData.event_id) ?? null}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, event_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Event" required />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenShowEventModal(true)}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Show Details */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={filteredShowNames}
                    getOptionLabel={(showName) => showName.name}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((showName) => showName.name)}
                    value={filteredShowNames.find((showName) => showName.id === formData.show_name_id) ?? null}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, show_name_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Show" required />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenShowNameModal(true)}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="show_date"
                  label="Date"
                  type="date"
                  value={formData.show_date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={showClasses}
                    getOptionLabel={(showClass) => showClass.name}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((showClass) => showClass.name)}
                    value={showClasses.find((showClass) => showClass.id === formData.class_id) ?? null}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, class_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Class" required />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenShowClassModal(true)}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="score"
                  label="Score"
                  type="number"
                  step="0.5"
                  value={formData.score}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="e.g., 143 or 69.5"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="placing"
                  label="Placing"
                  value={formData.placing}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="e.g., 1st, 2nd, Champion"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="earnings"
                  label="Earnings"
                  type="number"
                  step="0.01"
                  value={formData.earnings}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    disableClearable
                    options={riderOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    value={riderOptions.find((o) => o.id === formData.rider_id) ?? riderOptions[0]}
                    onChange={(e, newValue) =>
                      setFormData((prev) => ({ ...prev, rider_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Rider" />}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setOpenTrainerModal(true)}
                    sx={{ minWidth: 'auto', px: 1 }}
                  >
                    <AddIcon />
                  </Button>
                </Box>
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="notes"
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.horse_id || !formData.event_id || !formData.show_name_id || !formData.show_date || !formData.class_id}
          >
            {editingRecord ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Horse Modal */}
      <AddHorseModal
        open={openHorseModal}
        onClose={() => setOpenHorseModal(false)}
        onSuccess={handleAddHorse}
      />

      {/* Add Trainer Modal */}
      <AddTrainerModal
        open={openTrainerModal}
        onClose={() => setOpenTrainerModal(false)}
        onSuccess={handleAddTrainer}
      />

      {/* Add Reference Modals */}
      <AddReferenceModal
        open={openShowEventModal}
        onClose={() => setOpenShowEventModal(false)}
        apiCall={referenceAPI.createShowEvent}
        onSuccess={handleAddShowEvent}
        title="Show Event"
        label="Event Name"
      />

      <AddReferenceModal
        open={openShowNameModal}
        onClose={() => setOpenShowNameModal(false)}
        apiCall={handleCreateShowName}
        onSuccess={handleAddShowName}
        title="Show Name"
        label="Show Name"
      />

      <AddReferenceModal
        open={openShowClassModal}
        onClose={() => setOpenShowClassModal(false)}
        apiCall={referenceAPI.createShowClass}
        onSuccess={handleAddShowClass}
        title="Show Class"
        label="Class Name"
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Show Record"
        message="Permanently delete this show record? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default ShowsPage;

